import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Zap, ArrowRight, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_DURATION_MS = 10 * 60 * 1000; // 10 minutes
const WARNING_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

const FEATURES = [
  { id: 'dashboard', label: 'Dashboard', path: 'dashboard' },
  { id: 'generate', label: 'Generate Content', path: 'generate' },
  { id: 'quora', label: 'Quora Goldmine', path: 'quora' },
  { id: 'referrals', label: 'Referrals', path: 'referrals' },
  { id: 'resources', label: 'Free Resources', path: 'resources' },
];

const getSessionId = () => {
  let sid = sessionStorage.getItem('demo_session_id');
  if (!sid) {
    sid = `demo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('demo_session_id', sid);
  }
  return sid;
};

const trackEvent = (eventType, feature = null, metadata = {}) => {
  const sid = getSessionId();
  axios.post(`${API}/demo-analytics/event`, {
    session_id: sid,
    event_type: eventType,
    feature,
    metadata,
  }).catch(() => {}); // fire-and-forget
};

export default function DemoBanner({ onExpired, onSignUp }) {
  const [timeLeft, setTimeLeft] = useState(DEMO_DURATION_MS);
  const [explored, setExplored] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('demo_explored') || '[]');
    } catch { return []; }
  });
  const [dismissed, setDismissed] = useState(false);
  const [showExpiry, setShowExpiry] = useState(false);

  // Get start time from sessionStorage
  useEffect(() => {
    const start = sessionStorage.getItem('demo_start');
    if (!start) {
      sessionStorage.setItem('demo_start', Date.now().toString());
      // Capture UTM params
      const params = new URLSearchParams(window.location.search);
      const utmData = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(k => {
        if (params.get(k)) utmData[k] = params.get(k);
      });
      trackEvent('session_start', null, utmData);
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      const start = parseInt(sessionStorage.getItem('demo_start') || Date.now());
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, DEMO_DURATION_MS - elapsed);
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        trackEvent('session_end', null, {
          features_explored: JSON.parse(sessionStorage.getItem('demo_explored') || '[]').length,
          last_feature: JSON.parse(sessionStorage.getItem('demo_explored') || '[]').slice(-1)[0] || 'none',
          ended_by: 'timer',
        });
        setShowExpiry(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Track feature exploration
  const trackFeature = useCallback((featureId) => {
    setExplored(prev => {
      if (prev.includes(featureId)) return prev;
      const next = [...prev, featureId];
      sessionStorage.setItem('demo_explored', JSON.stringify(next));
      trackEvent('feature_visit', featureId, { explored_count: next.length });
      return next;
    });
  }, []);

  // Expose tracking globally
  useEffect(() => {
    window.__demoTrackFeature = trackFeature;
    return () => { delete window.__demoTrackFeature; };
  }, [trackFeature]);

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);
  const isWarning = timeLeft <= WARNING_THRESHOLD_MS && timeLeft > 0;
  const progress = explored.length;
  const total = FEATURES.length;

  // Expiry overlay
  if (showExpiry) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demo-expired-overlay">
        <div className="bg-gray-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Zap className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Demo Session Ended</h2>
          <p className="text-gray-400 mb-2">
            You explored <span className="text-purple-400 font-semibold">{progress} of {total}</span> features
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {progress < total
              ? `There's ${total - progress} more to discover. Create your free account to unlock everything.`
              : "You've seen it all! Imagine this powered by YOUR data."
            }
          </p>
          <button
            onClick={() => { trackEvent('cta_click', null, { cta: 'signup_expiry', features_explored: progress }); onSignUp(); }}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all mb-3"
            data-testid="demo-signup-btn"
          >
            Create My Free Account <ArrowRight className="inline w-4 h-4 ml-1" />
          </button>
          <button
            onClick={() => { trackEvent('cta_click', null, { cta: 'back_homepage', features_explored: progress }); onExpired(); }}
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            data-testid="demo-back-landing-btn"
          >
            Back to homepage
          </button>
        </div>
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <div
      className={`sticky top-0 z-[100] transition-all duration-300 ${
        isWarning
          ? 'bg-gradient-to-r from-red-900/90 to-orange-900/90 border-b border-red-500/40'
          : 'bg-gradient-to-r from-purple-900/90 to-indigo-900/90 border-b border-purple-500/30'
      } backdrop-blur-xl`}
      data-testid="demo-banner"
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-4">
        {/* Left: Demo badge + timer */}
        <div className="flex items-center gap-3">
          <span className="px-2 py-0.5 bg-purple-500/30 border border-purple-400/40 text-purple-300 text-xs font-bold rounded-full uppercase tracking-wider">
            Demo Mode
          </span>
          <div className={`flex items-center gap-1.5 text-sm font-mono ${isWarning ? 'text-red-300 animate-pulse' : 'text-gray-300'}`}>
            <Clock className="w-3.5 h-3.5" />
            <span>{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</span>
          </div>
        </div>

        {/* Center: Progress */}
        <div className="hidden sm:flex items-center gap-2 flex-1 max-w-xs">
          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-400 to-cyan-400 rounded-full transition-all duration-500"
              style={{ width: `${(progress / total) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {progress}/{total} explored
          </span>
        </div>

        {/* Right: CTA + dismiss */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => { trackEvent('cta_click', null, { cta: 'signup_banner', features_explored: progress }); onSignUp(); }}
            className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-all border border-white/10"
            data-testid="demo-banner-signup-btn"
          >
            Sign Up Free <ArrowRight className="inline w-3 h-3 ml-0.5" />
          </button>
          <button
            onClick={() => { trackEvent('banner_dismiss'); setDismissed(true); }}
            className="text-gray-500 hover:text-gray-300 transition-colors"
            data-testid="demo-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
